/*
 * Carbisdale - DialogManager
 *
 * Verwaltet modale Dialoge im Terminal-Stil:
 * - Bestätigungs-Dialoge (Ja/Nein)
 * - Info-Dialoge (OK)
 *
 * Alle Dialoge sind Promise-basiert und blockieren die weitere
 * Verarbeitung bis der User eine Auswahl getroffen hat.
 */

import { Style } from './config.js'
import { blurKeyboardSink } from './touchKeyboard.js'

/**
 * Dialog-Typen
 */
export const DialogType = {
    CONFIRM: 'confirm',
    INFO: 'info'
}

/**
 * DialogManager-Klasse
 */
export class DialogManager {
    /**
     * @param {Overlay} overlay - Der Overlay für die Darstellung
     */
    constructor(overlay) {
        this.overlay = overlay

        // Dialog-State
        this.isOpen = false
        this.type = null
        this.resolve = null  // Promise-Resolver

        // Confirm-Dialog State
        this.confirmSelectedButton = 0  // 0 = Yes, 1 = No

        // Dialog-Dimensionen (für Rendering)
        this.dialogX = 0
        this.dialogY = 0
        this.dialogWidth = 0
        this.dialogHeight = 0

        // Hover-State für Maus (-1 = kein Hover, 0 = erster Button, 1 = zweiter Button)
        this.hoveredButton = -1
    }

    // ===== Public API =====

    /**
     * Zeigt einen Bestätigungs-Dialog.
     * @param {string} title - Dialog-Titel
     * @param {string} message - Hauptnachricht
     * @param {string} subMessage - Unternachricht (optional)
     * @returns {Promise<boolean>} true wenn "Yes" gewählt
     */
    showConfirm(title, message, subMessage = '') {
        // Phase 6.0d: Soft-Keyboard schliessen, falls offen — Dialog sonst evtl.
        // von Tastatur verdeckt, Confirm-Buttons unerreichbar.
        blurKeyboardSink()
        return new Promise((resolve) => {
            this.isOpen = true
            this.type = DialogType.CONFIRM
            this.resolve = resolve
            this.confirmSelectedButton = 0  // Default: Yes

            // Args fuer _reRender (Hover- und Tab-Switch-Pfade) merken
            this._title = title
            this._message = message
            this._subMessage = subMessage

            this._renderConfirm(title, message, subMessage)
        })
    }

    /**
     * Zeigt einen Info-Dialog.
     * @param {string} title - Dialog-Titel
     * @param {string} message - Hauptnachricht
     * @param {string} subMessage - Unternachricht (optional)
     * @returns {Promise<void>}
     */
    showInfo(title, message, subMessage = '') {
        // Phase 6.0d: Soft-Keyboard schliessen, falls offen
        blurKeyboardSink()
        return new Promise((resolve) => {
            this.isOpen = true
            this.type = DialogType.INFO
            this.resolve = resolve

            // Args fuer _reRender (Hover-Update) merken
            this._title = title
            this._message = message
            this._subMessage = subMessage

            this._renderInfo(title, message, subMessage)
        })
    }

    /**
     * Prüft ob ein Dialog offen ist.
     * @returns {boolean}
     */
    isDialogOpen() {
        return this.isOpen
    }

    /**
     * Verarbeitet einen Tastendruck.
     * @param {KeyboardEvent} e
     * @returns {boolean} true wenn Event behandelt wurde
     */
    handleKeydown(e) {
        if (!this.isOpen) return false

        switch (this.type) {
            case DialogType.CONFIRM:
                return this._handleConfirmKey(e)
            case DialogType.INFO:
                return this._handleInfoKey(e)
        }

        return false
    }

    // ===== Confirm Dialog =====
    // Button-Layout-Konstanten (Phase-1 Primitive): [Y] Yes (7 Cells)
    // + 4 Cells Gap + [N] No (6 Cells) = 17 Cells Gesamtbreite. Werden
    // sowohl im Render- als auch im Hit-Test-Pfad benutzt.

    _renderConfirm(title, message, subMessage) {
        // Breite berechnen
        const minWidth = 30
        const contentWidth = Math.max(
            title.length,
            message.length,
            subMessage.length,
            17  // "[Y] Yes    [N] No"
        )
        const width = Math.max(minWidth, contentWidth + 4)
        const height = subMessage ? 7 : 6

        // Zentriert positionieren
        const x = Math.floor((this.overlay.cols - width) / 2)
        const y = Math.floor((this.overlay.rows - height) / 2)

        this.dialogX = x
        this.dialogY = y
        this.dialogWidth = width
        this.dialogHeight = height

        this.overlay.clear()

        // Box als Phase-1-Frame (proportional im Prop-Mode, Mono-Fallback sonst)
        this.overlay.drawMenuFrame(
            { x, y, width, height },
            { title, borderStyle: Style.DIALOG_BORDER }
        )

        // Nachrichten als Text-Spans (zentriert, proportional)
        let currentY = y + 2
        this.overlay.addTextSpan(x + 1, currentY, message, Style.DIALOG_TEXT, width - 2, { align: 'center' })

        if (subMessage) {
            currentY++
            this.overlay.addTextSpan(x + 1, currentY, subMessage, Style.MENU_ITEM, width - 2, { align: 'center' })
        }

        // Buttons
        this._renderConfirmButtons(x, y + height - 2, width)

        // Overlay explizit aktivieren und als dirty markieren
        this.overlay.isActive = true
        this.overlay.markDirty()
    }

    _renderConfirmButtons(boxX, y, boxWidth) {
        // Hover überschreibt Keyboard-Selection
        const activeButton = this.hoveredButton >= 0 ? this.hoveredButton : this.confirmSelectedButton

        const yesWidth = 7  // [Y] Yes
        const noWidth = 6   // [N] No
        const gap = 4
        const totalLen = yesWidth + gap + noWidth
        const yesX = boxX + Math.floor((boxWidth - totalLen) / 2)
        const noX = yesX + yesWidth + gap

        // Bounds fuer Click/Hover merken
        this._buttonY = y
        this._yesButtonX = yesX
        this._yesButtonW = yesWidth
        this._noButtonX = noX
        this._noButtonW = noWidth

        this.overlay.drawMenuItem(
            yesX, y,
            { key: 'Y', label: 'Yes' },
            { selected: activeButton === 0, itemWidth: yesWidth }
        )
        this.overlay.drawMenuItem(
            noX, y,
            { key: 'N', label: 'No' },
            { selected: activeButton === 1, itemWidth: noWidth }
        )
    }

    _handleConfirmKey(e) {
        const key = e.key.toLowerCase()

        switch (key) {
            case 'y':
                this._closeDialog(true)
                return true

            case 'n':
                this._closeDialog(false)
                return true

            case 'arrowleft':
            case 'arrowright':
            case 'tab':
                this.confirmSelectedButton = 1 - this.confirmSelectedButton
                // drawMenuItem pusht additiv in stripes[]/frames[] — der
                // Stripe-Wechsel braucht einen kompletten Re-Render mit
                // overlay.clear() (passiert in _renderConfirm).
                this._reRender()
                return true

            case 'enter':
                this._closeDialog(this.confirmSelectedButton === 0)
                return true

            case 'escape':
                this._closeDialog(false)
                return true
        }

        return true  // Alle Tasten im Dialog abfangen
    }

    // ===== Info Dialog =====

    _renderInfo(title, message, subMessage) {
        // Breite berechnen
        const minWidth = 30
        const contentWidth = Math.max(
            title.length,
            message.length,
            subMessage.length,
            8  // "[OK]"
        )
        const width = Math.max(minWidth, contentWidth + 4)
        const height = subMessage ? 6 : 5

        // Zentriert positionieren
        const x = Math.floor((this.overlay.cols - width) / 2)
        const y = Math.floor((this.overlay.rows - height) / 2)

        this.dialogX = x
        this.dialogY = y
        this.dialogWidth = width
        this.dialogHeight = height

        this.overlay.clear()

        // Box als Phase-1-Frame
        this.overlay.drawMenuFrame(
            { x, y, width, height },
            { title, borderStyle: Style.DIALOG_BORDER }
        )

        // Nachrichten als Text-Spans (zentriert, proportional)
        let currentY = y + 2
        this.overlay.addTextSpan(x + 1, currentY, message, Style.DIALOG_TEXT, width - 2, { align: 'center' })

        if (subMessage) {
            currentY++
            this.overlay.addTextSpan(x + 1, currentY, subMessage, Style.MENU_ITEM, width - 2, { align: 'center' })
        }

        // OK Button
        this._renderInfoButton(x, y + height - 2, width)

        // Overlay explizit aktivieren und als dirty markieren
        this.overlay.isActive = true
        this.overlay.markDirty()
    }

    _renderInfoButton(boxX, y, boxWidth) {
        // [OK] als Phase-1-Item ohne `key`-Cell — Label uebernimmt den ganzen
        // Text inkl. Brackets, Stripe-Highlight bei Hover.
        const okWidth = 4  // [OK]
        const okX = boxX + Math.floor((boxWidth - okWidth) / 2)

        // Bounds fuer Click/Hover merken
        this._buttonY = y
        this._okButtonX = okX
        this._okButtonW = okWidth

        this.overlay.drawMenuItem(
            okX, y,
            { label: '[OK]' },
            { selected: this.hoveredButton === 0, itemWidth: okWidth }
        )
    }

    _handleInfoKey(e) {
        const key = e.key.toLowerCase()

        // Jede Taste schließt den Info-Dialog
        if (key === 'enter' || key === 'escape' || key === 'o' || key === ' ') {
            this._closeDialog()
            return true
        }

        return true  // Alle Tasten abfangen
    }

    // ===== Maus-Handling =====

    /**
     * Verarbeitet einen Mausklick.
     * @param {number} charX - Character-X-Position
     * @param {number} charY - Character-Y-Position
     * @returns {boolean} true wenn Event behandelt wurde
     */
    handleClick(charX, charY) {
        if (!this.isOpen) return false

        switch (this.type) {
            case DialogType.CONFIRM:
                return this._handleConfirmClick(charX, charY)
            case DialogType.INFO:
                return this._handleInfoClick(charX, charY)
        }

        return true
    }

    /**
     * Verarbeitet Maus-Bewegung für Hover-Effekte.
     * @param {number} charX - Character-X-Position
     * @param {number} charY - Character-Y-Position
     * @returns {boolean} true wenn über einem Button
     */
    handleMouseMove(charX, charY) {
        if (!this.isOpen) return false

        let newHover = -1
        let overButton = false

        if (this.type === DialogType.CONFIRM && charY === this._buttonY) {
            if (charX >= this._yesButtonX && charX < this._yesButtonX + this._yesButtonW) {
                newHover = 0
                overButton = true
            } else if (charX >= this._noButtonX && charX < this._noButtonX + this._noButtonW) {
                newHover = 1
                overButton = true
            }
        } else if (this.type === DialogType.INFO && charY === this._buttonY) {
            if (charX >= this._okButtonX && charX < this._okButtonX + this._okButtonW) {
                newHover = 0
                overButton = true
            }
        }

        // Nur neu rendern wenn sich Hover geändert hat
        if (newHover !== this.hoveredButton) {
            this.hoveredButton = newHover

            // Hover-Rerender: clear() + komplettes Render der aktuellen
            // Dialog-Variante. drawMenuItem schreibt in stripes[]/cells/spans
            // additiv — ohne clear() wuerden sich die Frames aufaddieren.
            // Wir cachen Title/Messages nicht, also den ganzen Pfad noch
            // einmal durchlaufen.
            this._reRender()
        }

        return overButton
    }

    /**
     * Neuzeichnen der aktuellen Dialog-Variante (Hover-Update etc.).
     * Spart die State-Argumente, die in _renderConfirm/_renderInfo nicht
     * mehr im Aufrufer-Scope liegen.
     */
    _reRender() {
        if (this.type === DialogType.CONFIRM) {
            this._renderConfirm(this._title, this._message, this._subMessage)
        } else if (this.type === DialogType.INFO) {
            this._renderInfo(this._title, this._message, this._subMessage)
        }
    }

    _handleConfirmClick(charX, charY) {
        if (charY !== this._buttonY) return true

        if (charX >= this._yesButtonX && charX < this._yesButtonX + this._yesButtonW) {
            this._closeDialog(true)
            return true
        }

        if (charX >= this._noButtonX && charX < this._noButtonX + this._noButtonW) {
            this._closeDialog(false)
            return true
        }

        return true
    }

    _handleInfoClick(charX, charY) {
        // Klick irgendwo im Dialog schließt ihn (wie jede Taste)
        this._closeDialog()
        return true
    }

    // ===== Helpers =====

    _closeDialog(result = undefined) {
        this.isOpen = false
        this.type = null
        this.hoveredButton = -1
        this.overlay.deactivate()

        if (this.resolve) {
            this.resolve(result)
            this.resolve = null
        }
    }
}
